import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, AlertCircle, CheckCircle, Send } from 'lucide-react';
import api from '../utils/api';
import PublicFooter from '../components/PublicFooter';

const RequestInvite = () => {
  const [form, setForm] = useState({ contactName: '', companyName: '', email: '', phone: '', companyWebsite: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const handleChange = event => setForm(previous => ({ ...previous, [event.target.name]: event.target.value }));

  const handleSubmit = async event => {
    event.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/request-invite', form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Your request could not be sent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 px-4 py-8 sm:py-12">
      <main className="mx-auto w-full max-w-lg">
        <div className="mb-6 text-center">
          <div className="mb-4 inline-flex h-24 w-24 items-center justify-center rounded-2xl bg-white p-3 shadow-lg">
            <img src="/ttic-logo.png" alt="Texan Title Insurance Company" className="h-full w-full object-contain" />
          </div>
          <h1 className="font-display text-3xl font-bold text-white">{submitted ? 'Request received' : 'Request an invite code'}</h1>
          <p className="mt-2 text-white/80">{submitted ? 'Thank you for your interest in the Marketing Hub.' : 'Branded marketing content for your title company starts here.'}</p>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
          {submitted ? (
            <div role="status" className="text-center">
              <CheckCircle size={44} className="mx-auto mb-4 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">You're on our list.</h2>
              <p className="mt-3 text-gray-600">The TTIC team has received your request. We'll review it and follow up at <strong className="break-all text-gray-900">{form.email.trim()}</strong> with next steps.</p>
              <p className="mt-3 text-sm text-gray-500">Already working with a TTIC Agency Rep? You can also ask them for an invite code.</p>
              <Link to="/register" className="btn-primary mt-6 flex w-full items-center justify-center gap-2">I have a code <ArrowRight size={17} /></Link>
              <Link to="/login" className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-primary-600"><ArrowLeft size={16} /> Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="text-sm leading-relaxed text-gray-600">Tell us a little about yourself. The TTIC team will review your request and help you get started.</p>
              {error && <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700"><AlertCircle size={18} className="shrink-0" />{error}</div>}
              {[
                { name: 'contactName', label: 'Your name', placeholder: 'First and last name', type: 'text', maxLength: 255, autoComplete: 'name', required: true },
                { name: 'companyName', label: 'Title company', placeholder: 'Your title company name', type: 'text', maxLength: 255, autoComplete: 'organization', required: true },
                { name: 'email', label: 'Work email', placeholder: 'you@company.com', type: 'email', maxLength: 254, autoComplete: 'email', required: true },
                { name: 'phone', label: 'Phone number (optional)', placeholder: '(555) 123-4567', type: 'tel', maxLength: 50, autoComplete: 'tel', required: false }
              ].map(field => (
                <div key={field.name}>
                  <label htmlFor={`request-${field.name}`} className="mb-2 block text-sm font-medium text-gray-700">{field.label}</label>
                  <input id={`request-${field.name}`} name={field.name} type={field.type} value={form[field.name]} onChange={handleChange} placeholder={field.placeholder} maxLength={field.maxLength} autoComplete={field.autoComplete} required={field.required} className="input-field" />
                </div>
              ))}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="request-company-website">Leave this field empty</label>
                <input id="request-company-website" name="companyWebsite" value={form.companyWebsite} onChange={handleChange} tabIndex={-1} autoComplete="off" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary flex w-full items-center justify-center gap-2">
                {loading ? 'Sending request...' : <><Send size={17} /> Send code request</>}
              </button>
              <p className="text-xs leading-relaxed text-gray-500">Sending a request does not create an account. We'll contact you about access using the information above.</p>
              <div className="border-t border-gray-100 pt-4 text-center">
                <Link to="/register" className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-primary-600">Already have a code? Create account <ArrowRight size={16} /></Link>
                <br /><Link to="/login" className="inline-flex min-h-11 items-center gap-2 text-sm text-gray-600"><ArrowLeft size={16} /> Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
        <PublicFooter dark compact />
      </main>
    </div>
  );
};

export default RequestInvite;
